import { getChatApiBase, getChatApiHeaders } from './chatApiClient'
import axios from './axiosInstance'
import constants from '../constants'
import { isAuthenticated } from '../utils/authSession'

export default function init() {
  console.log('moderation init')
}

export async function listRules(app, paginationPayload = '') {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/rules?limit=50&${paginationPayload}`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function listActivities(
  app,
  apiHost,
  paginationPayload = '',
  startRange = '',
  endRange = '',
  filters = {},
) {
  let URL = `${getChatApiBase(
    app,
    apiHost,
  )}/moderation/blocked-messages?limit=10&${paginationPayload}`
  if (startRange) URL += `&start=${startRange}`
  if (endRange) URL += `&end=${endRange}`
  if (filters.ruleId && filters.ruleId.length > 0) {
    URL += `&ruleId=${filters.ruleId.map(v => encodeURIComponent(v)).join(',')}`
  }
  try {
    const res = await axios.get(URL, {
      headers: getChatApiHeaders(app),
    })
    return res.data
  } catch (error) {
    return error.response?.data || false
  }
}

// Generic helper for moderation GET endpoints using only Bearer auth now (apiKey removed)

export async function listFlaggedMessages(
  app,
  apiHost,
  paginationPayload = '',
  startRange = '',
  endRange = '',
  filters = {},
) {
  let URL = `${getChatApiBase(
    app,
    apiHost,
  )}/moderation/flagged-messages?limit=10&${paginationPayload}`
  if (startRange) URL += `&start=${startRange}`
  if (endRange) URL += `&end=${endRange}`
  if (filters.reasonId && filters.reasonId.length > 0) {
    URL += `&reasonId=${filters.reasonId.map(v => encodeURIComponent(v)).join(',')}`
  }
  if (filters.ruleId && filters.ruleId.length > 0) {
    URL += `&ruleId=${filters.ruleId.map(v => encodeURIComponent(v)).join(',')}`
  }
  try {
    const res = await axios.get(URL, {
      headers: getChatApiHeaders(app),
    })
    return res.data
  } catch (error) {
    return error.response?.data || false
  }
}

export async function listReviewedMessages(
  app,
  apiHost,
  paginationPayload = '',
  startRange = '',
  endRange = '',
  filters = {},
) {
  let URL = `${getChatApiBase(
    app,
    apiHost,
  )}/moderation/reviewed-messages?limit=10&${paginationPayload}`
  if (startRange) URL += `&start=${startRange}`
  if (endRange) URL += `&end=${endRange}`
  if (filters.reasonId && filters.reasonId.length > 0) {
    URL += `&reasonId=${filters.reasonId.map(v => encodeURIComponent(v)).join(',')}`
  }
  if (filters.ruleId && filters.ruleId.length > 0) {
    URL += `&ruleId=${filters.ruleId.map(v => encodeURIComponent(v)).join(',')}`
  }
  if (filters.action && filters.action.length > 0) {
    URL += `&action=${filters.action.map(v => encodeURIComponent(v)).join(',')}`
  }
  try {
    const res = await axios.get(URL, {
      headers: getChatApiHeaders(app),
    })
    return res.data
  } catch (error) {
    return error.response?.data || false
  }
}

// Accept (mark as reviewed) a flagged message
export async function acceptFlaggedMessage(app, messageId) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/flagged-messages/${messageId}/accept`
  try {
    if (isAuthenticated()) {
      const res = await axios.post(URL, {})
      return res.data
    }
  } catch (error) {
    return error.response?.data || false
  }
  return false // Not authenticated — the call was never made
}

// Approve only (without reviewed flag) for flagged message
export async function approveFlaggedMessage(app, messageId) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/flagged-messages/${messageId}`
  const patchData = { status: 'approved' }
  try {
    if (isAuthenticated()) {
      const res = await axios.patch(URL, patchData)
      return res.data
    }
  } catch (error) {
    return error.response?.data || false
  }
  return false // Not authenticated — the call was never made
}

// Review (approve) flagged message using PATCH pattern (status + reviewed)
export async function reviewFlaggedMessage(app, messageId) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/flagged-messages/${messageId}`
  const patchData = { status: 'approved', reviewed: true }
  try {
    if (isAuthenticated()) {
      const res = await axios.patch(URL, patchData)
      return res.data
    }
  } catch (error) {
    return error.response?.data || false
  }
  return false // Not authenticated — the call was never made
}

export async function blockFlaggedMessage(app, messageId) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/flagged-messages/${messageId}`
  const patchData = { status: 'disapproved' }
  try {
    if (isAuthenticated()) {
      const res = await axios.patch(URL, patchData)
      return res.data
    }
  } catch (error) {
    return error.response?.data || false
  }
  return false // Not authenticated — the call was never made
}

export async function reviewFlaggedMessageFallback(
  app,
  messageId,
  body = { status: 'approved', reviewed: true },
) {
  const URL = `${constants.customerDomain}apps/${app.id}/flagged-messages/${messageId}`
  try {
    if (isAuthenticated()) {
      const res = await axios.patch(URL, body, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return res.data
    }
  } catch (error) {
    return error.response?.data || false
  }
  return false
}

// Ban sender of a flagged message (placeholder endpoint - adjust if actual differs)
export async function banSenderOfFlaggedMessage(app, messageId) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/flagged-messages/${messageId}/ban-sender`
  try {
    if (isAuthenticated()) {
      const res = await axios.post(URL, {})
      return res.data
    }
  } catch (error) {
    return error.response?.data || false
  }
  return false
}

export async function getRule(id, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/rules/${id}`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function listRuleRevisions(id, app, pageNo = 1, searchText = '') {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/rules/${id}/revisions?limit=5&page=${pageNo}&searchKey=${searchText}`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function deleteRule(id, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/rules/${id}`
  try {
    if (isAuthenticated()) {
      const res = await axios.delete(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function updateRule(id, post, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/rules/${id}`
  try {
    if (isAuthenticated()) {
      const res = await axios.put(URL, post)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function listKeywords(app, paginationPayload = '') {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/keywords?limit=50&${paginationPayload}`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function deleteKeyword(id, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/keywords/${id}`
  try {
    if (isAuthenticated()) {
      const res = await axios.delete(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function addKeyword(post, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/keywords`
  try {
    if (isAuthenticated()) {
      const res = await axios.post(URL, post)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function updateKeyword(id, post, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/keywords/${id}`
  try {
    if (isAuthenticated()) {
      const res = await axios.put(URL, post)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function getKeyword(id, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/keywords/${id}`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function listConfiguration(app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/configuration`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function addRule(post, app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/rules`
  try {
    if (isAuthenticated()) {
      const res = await axios.post(URL, post)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function addCustomCategory(post, app, apiHost) {
  const URL = `${getChatApiBase(app, apiHost)}/moderation/reasons`

  return axios
    .post(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => {
      return res.data
    })
    .catch(error => {
      return error.response.data
    })
}

export async function getCustomCategories(app, apiHost) {
  const URL = `${getChatApiBase(app, apiHost)}/moderation/reasons`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => {
      return res.data
    })
    .catch(error => {
      return error
    })
}

export async function deleteCustomCategory(id, app, apiHost) {
  const URL = `${getChatApiBase(app, apiHost)}/moderation/reasons/${id}`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => {
      return res.data
    })
    .catch(error => {
      return error.response.data
    })
}

export async function updateCustomCategory(id, post, app, apiHost) {
  const URL = `${getChatApiBase(app, apiHost)}/moderation/reasons/${id}`
  return axios
    .put(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => {
      return res.data
    })
    .catch(error => {
      return error.response.data
    })
}

export async function updateBlockedMessageStatus(id, status, app, isReviewed = false) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/blocked-messages/${id}`
  const patchData = isReviewed ? { status, reviewed: true } : { status }
  try {
    if (isAuthenticated()) {
      const res = await axios.patch(URL, patchData)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function updateGlobalSettings(app, post) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/global-settings`
  try {
    if (isAuthenticated()) {
      const res = await axios.put(URL, post)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function getGlobalSettings(app) {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/global-settings`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}

export async function getFlaggedUsersOfMessage(app, id, paginationPayload = '') {
  const URL = `${constants.customerDomain}apps/${app.id}/moderation/${id}/flagged-users?limit=${
    constants.flaggedByUsersTableCount
  }${paginationPayload ? '&' : ''}${paginationPayload}`
  try {
    if (isAuthenticated()) {
      const res = await axios.get(URL)
      return res.data
    }
  } catch (error) {
    return error.response.data
  }
  return false // Not authenticated — the call was never made
}
